/**
 * Workspace host resolution (docs/decisions.md #20).
 *
 * One wildcard DNS record (`*.obviouscrm.com` → the frontend) serves every
 * workspace; which workspace a request belongs to is decided here, from the
 * hostname, and then checked against the control-plane registry. Unknown
 * slugs render "workspace not found" - DNS is never the gatekeeper.
 */

/** Subdomains that can never be tenant slugs. */
export const RESERVED_SLUGS = [
  "hq",
  "www",
  "app",
  "api",
  "forms",
  "mail",
  "status",
] as const;

export type WorkspaceHost =
  /** The operator console: hq.<base>. */
  | { kind: "hq" }
  /** A tenant workspace candidate - verify the slug against the registry. */
  | { kind: "tenant"; slug: string }
  /** The bare/base domain (marketing site / redirect). */
  | { kind: "apex" }
  /** Local dev, previews, or a host outside the base domain. */
  | { kind: "external"; hostname: string };

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** True when a string is a well-formed, non-reserved tenant slug. */
export const isWorkspaceSlug = (s: string): boolean =>
  SLUG_RE.test(s) &&
  s.length <= 40 &&
  !(RESERVED_SLUGS as readonly string[]).includes(s);

/**
 * Classify a hostname against the base domain.
 *
 * - `hq.obviouscrm.com` → hq
 * - `queencitydental.obviouscrm.com` → tenant "queencitydental"
 * - `obviouscrm.com` / `www.obviouscrm.com` → apex
 * - reserved or malformed subdomains (`api.`, `a.b.`) → external
 * - `localhost`, Render preview hosts, custom client domains → external
 *   (custom domains resolve through the registry's hostname map instead)
 */
export const parseWorkspaceHost = (
  hostname: string,
  baseDomain = "obviouscrm.com",
): WorkspaceHost => {
  const host = hostname.toLowerCase().split(":")[0]!;
  const base = baseDomain.toLowerCase();

  if (host === base || host === `www.${base}`) return { kind: "apex" };
  if (!host.endsWith(`.${base}`)) return { kind: "external", hostname: host };

  const sub = host.slice(0, -(base.length + 1));
  if (sub === "hq") return { kind: "hq" };
  if (sub.includes(".")) return { kind: "external", hostname: host };
  if ((RESERVED_SLUGS as readonly string[]).includes(sub))
    return { kind: "external", hostname: host };
  if (!SLUG_RE.test(sub)) return { kind: "external", hostname: host };
  return { kind: "tenant", slug: sub };
};
